import type { Course, EnrolledCourse, CourseType, CourseLevel } from '@/types/course'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api'

export interface ListCoursesParams {
  type?: CourseType
  level?: CourseLevel
  category?: string
  /**
   * Status do curso. Aceita 1 valor (ex: 'published') OU lista CSV
   * (ex: 'published,coming_soon') — backend faz split em listCourses.
   * Tipado como string pra permitir CSV; valores válidos:
   * 'draft' | 'published' | 'archived' | 'coming_soon'.
   */
  status?: string
}

export const coursesService = {
  /**
   * Lista todos os cursos com filtros opcionais
   */
  async listCourses(params?: ListCoursesParams): Promise<Course[]> {
    const searchParams = new URLSearchParams()

    if (params?.type) searchParams.set('type', params.type)
    if (params?.level) searchParams.set('level', params.level)
    if (params?.category) searchParams.set('category', params.category)
    if (params?.status) searchParams.set('status', params.status)

    const url = `${API_URL}/courses${searchParams.toString() ? `?${searchParams}` : ''}`

    const response = await fetch(url)

    if (!response.ok) {
      throw new Error('Falha ao carregar cursos')
    }

    return response.json()
  },

  /**
   * Busca um curso por ID
   */
  async getCourseById(id: number): Promise<Course> {
    const response = await fetch(`${API_URL}/courses/${id}`)

    if (!response.ok) {
      throw new Error('Curso não encontrado')
    }

    return response.json()
  },

  /**
   * Busca um curso por slug
   */
  async getCourseBySlug(slug: string): Promise<Course> {
    const response = await fetch(`${API_URL}/courses/slug/${slug}`)

    if (!response.ok) {
      throw new Error('Curso não encontrado')
    }

    return response.json()
  },

  /**
   * Matricula o usuário autenticado em um curso
   */
  async enrollInCourse(courseId: number, token: string): Promise<void> {
    const response = await fetch(`${API_URL}/courses/${courseId}/enroll`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({}), // Envia objeto vazio como body
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Falha ao matricular no curso')
    }
  },

  /**
   * Remove matrícula do usuário autenticado
   */
  async unenrollFromCourse(courseId: number, token: string): Promise<void> {
    const response = await fetch(`${API_URL}/courses/${courseId}/enroll`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Falha ao cancelar matrícula')
    }
  },

  /**
   * Verifica se usuário está matriculado no curso
   */
  async checkEnrollment(courseId: number, token: string): Promise<boolean> {
    const response = await fetch(`${API_URL}/courses/${courseId}/is-enrolled`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
    })

    if (!response.ok) {
      return false
    }

    const data = await response.json()
    return data.isEnrolled
  },

  /**
   * Lista cursos matriculados do usuário autenticado
   */
  async getMyEnrolledCourses(token: string): Promise<EnrolledCourse[]> {
    const response = await fetch(`${API_URL}/me/courses`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Falha ao carregar cursos matriculados: ${response.status} ${errorText}`)
    }

    return response.json()
  },

  /**
   * Busca o próximo episódio a assistir baseado no progresso
   */
  async getNextEpisode(
    courseId: number,
    token: string
  ): Promise<{
    moduleId: number
    episodeNumber: number
    episodeTitle: string
    moduleTitle: string
    isFirstEpisode: boolean
    allCompleted?: boolean
  }> {
    const response = await fetch(`${API_URL}/courses/${courseId}/next-episode`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
    })

    if (!response.ok) {
      throw new Error('Falha ao buscar próximo episódio')
    }

    return response.json()
  },

  /**
   * Busca o último episódio assistido pelo usuário (qualquer curso)
   */
  async getLastWatched(token: string): Promise<{
    courseId: number
    courseTitle: string
    moduleId: number
    moduleTitle: string
    moduleOrder: number
    episodeId: number
    episodeNumber: number
    episodeTitle: string
    lastWatchedAt: string
    completed: boolean
    progressPercent: number
    completedEpisodes: number
    totalEpisodes: number
  } | null> {
    const response = await fetch(`${API_URL}/courses/last-watched`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
    })

    if (response.status === 204) {
      return null
    }

    if (!response.ok) {
      throw new Error('Falha ao buscar último episódio assistido')
    }

    return response.json()
  },
}
